import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  PhoneScreen,
  CountryInfo,
  UssdServiceRequest,
  UssdServiceResponse,
  COUNTRIES,
} from '../../types/ussd.models';
import { UssdService } from './ussd.service';

/**
 * Where the simulated handset session stands.
 * idle    — nothing dialled yet
 * dialing — the first request is on its way
 * open    — the menu is waiting for the subscriber
 * waiting — a reply is on its way
 * ended   — the application (or the tester) closed the session
 * failed  — the request could not be completed
 */
export type UssdSessionState = 'idle' | 'dialing' | 'open' | 'waiting' | 'ended' | 'failed';

/** One request and its response, as the transcript and inspector show them. */
export interface UssdTurn {
  id: number;
  sessionId: string;
  kind: 'New' | 'Continue' | 'Release';
  /** What the tester dialled or typed; empty for a release. */
  input: string;
  sentAt: Date;
  request: UssdServiceRequest;
  response: UssdServiceResponse | null;
  /** HTTP status when the request failed. */
  httpStatus: number | null;
  error: string | null;
  /** Round trip measured in the browser. */
  latencyMs: number | null;
  pending: boolean;
}

/** Turns kept in memory; older ones drop off the transcript. */
const MAX_TURNS = 120;

const now = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

@Injectable({ providedIn: 'root' })
export class UssdSimulatorStore {
  private ussdService = inject(UssdService);

  private _screen = new BehaviorSubject<PhoneScreen>('dialer');
  private _phoneNumber = new BehaviorSubject<string>('');
  private _dialedCode = new BehaviorSubject<string>('');
  private _sessionId = new BehaviorSubject<string | null>(null);
  private _ussdResponse = new BehaviorSubject<UssdServiceResponse | null>(null);
  private _isLoading = new BehaviorSubject<boolean>(false);
  private _error = new BehaviorSubject<string | null>(null);
  private _selectedCountry = new BehaviorSubject<CountryInfo>(COUNTRIES[0]);
  private _isConnecting = new BehaviorSubject<boolean>(false);
  private _currentServiceCode = new BehaviorSubject<string>('');
  private _operator = new BehaviorSubject<string>('');
  private _transcript = new BehaviorSubject<UssdTurn[]>([]);
  private _sessionState = new BehaviorSubject<UssdSessionState>('idle');
  private _sessionStartedAt = new BehaviorSubject<Date | null>(null);

  /** True once a country came from the tester, a link or a saved number. */
  countryChosen = false;
  private nextTurnId = 1;

  screen$ = this._screen.asObservable();
  phoneNumber$ = this._phoneNumber.asObservable();
  dialedCode$ = this._dialedCode.asObservable();
  sessionId$ = this._sessionId.asObservable();
  ussdResponse$ = this._ussdResponse.asObservable();
  isLoading$ = this._isLoading.asObservable();
  error$ = this._error.asObservable();
  selectedCountry$ = this._selectedCountry.asObservable();
  isConnecting$ = this._isConnecting.asObservable();
  operator$ = this._operator.asObservable();
  transcript$ = this._transcript.asObservable();
  sessionState$ = this._sessionState.asObservable();
  sessionStartedAt$ = this._sessionStartedAt.asObservable();

  get screen(): PhoneScreen { return this._screen.getValue(); }
  get phoneNumber(): string { return this._phoneNumber.getValue(); }
  get dialedCode(): string { return this._dialedCode.getValue(); }
  get sessionId(): string | null { return this._sessionId.getValue(); }
  get ussdResponse(): UssdServiceResponse | null { return this._ussdResponse.getValue(); }
  get isLoading(): boolean { return this._isLoading.getValue(); }
  get error(): string | null { return this._error.getValue(); }
  get selectedCountry(): CountryInfo { return this._selectedCountry.getValue(); }
  get isConnecting(): boolean { return this._isConnecting.getValue(); }
  get currentServiceCode(): string { return this._currentServiceCode.getValue(); }
  get operator(): string { return this._operator.getValue(); }
  get transcript(): UssdTurn[] { return this._transcript.getValue(); }
  get sessionState(): UssdSessionState { return this._sessionState.getValue(); }
  get sessionStartedAt(): Date | null { return this._sessionStartedAt.getValue(); }

  /** A session the network still holds open, so it needs a Release to end. */
  get isSessionLive(): boolean {
    const state = this._sessionState.getValue();
    return !!this._sessionId.getValue() && (state === 'dialing' || state === 'open' || state === 'waiting');
  }

  /** The full number sent as MSISDN, e.g. +26771234567. */
  get msisdn(): string {
    return this.buildMsisdn();
  }

  // ── Handset ──────────────────────────────────────────────

  handlePhoneClick(): void {
    this._screen.next('dialer');
  }

  handleConnect(): void {
    if (!this._phoneNumber.getValue()) return;
    this._isConnecting.next(false);
    this._screen.next('dialer');
  }

  handleDigitPress(digit: string): void {
    this._dialedCode.next(this._dialedCode.getValue() + digit);
  }

  handleDelete(): void {
    this._dialedCode.next(this._dialedCode.getValue().slice(0, -1));
  }

  setDialedCode(code: string): void {
    this._dialedCode.next((code ?? '').replace(/[^\d*#+]/g, ''));
  }

  /** Dial what is on the handset. */
  handleCall(): void {
    this.dial();
  }

  /**
   * Start a session for `code` (or the dialled code). Any session still open
   * is released first, as a handset would on a new call.
   */
  dial(code: string = this._dialedCode.getValue()): void {
    const serviceCode = (code ?? '').trim();
    if (!serviceCode) return;
    if (!this._phoneNumber.getValue()) {
      this._error.next('Enter a test number before you dial.');
      return;
    }

    if (this.isSessionLive) this.release();

    const sessionId = this.ussdService.generateSessionId();
    this._dialedCode.next(serviceCode);
    this._sessionId.next(sessionId);
    this._currentServiceCode.next(serviceCode);
    this._sessionStartedAt.next(new Date());
    this._sessionState.next('dialing');
    this._ussdResponse.next(null);
    this._screen.next('ussd');
    this._isLoading.next(true);
    this._error.next(null);

    const request = this.ussdService.createInitialRequest(
      this.buildMsisdn(),
      serviceCode,
      sessionId,
      this._operator.getValue() || undefined,
      this._selectedCountry.getValue().code
    );
    this.send('New', serviceCode, request);
  }

  /** Answer the menu on screen. */
  handleUSSDSend(userInput: string): void {
    this.reply(userInput);
  }

  reply(userInput: string): void {
    const sessionId = this._sessionId.getValue();
    if (!sessionId || this._sessionState.getValue() !== 'open') return;

    this._isLoading.next(true);
    this._sessionState.next('waiting');

    const request = this.ussdService.createContinueRequest(
      this.buildMsisdn(),
      this._currentServiceCode.getValue(),
      sessionId,
      userInput,
      this._operator.getValue() || undefined,
      this._selectedCountry.getValue().code
    );
    this.send('Continue', userInput, request);
  }

  /**
   * Tell the network the subscriber hung up. Only sent while the session is
   * still open; the response is logged but never reopens anything.
   */
  release(): void {
    const sessionId = this._sessionId.getValue();
    if (!sessionId || !this.isSessionLive) return;

    this._sessionState.next('ended');
    this._isLoading.next(false);

    const request = this.ussdService.createEndRequest(
      this.buildMsisdn(),
      this._currentServiceCode.getValue(),
      sessionId,
      this._operator.getValue() || undefined,
      this._selectedCountry.getValue().code
    );
    this.send('Release', '', request);
  }

  /** Cancel on the handset: end the session and go back to the dialler. */
  handleUSSDCancel(): void {
    this.release();
    this._screen.next('dialer');
    this._sessionId.next(null);
    this._ussdResponse.next(null);
    this._isLoading.next(false);
    this._error.next(null);
    if (this._sessionState.getValue() !== 'failed') this._sessionState.next('ended');
  }

  /** Hang up and keep the transcript; used when leaving the page. */
  hangUp(): void {
    this.release();
    this._screen.next('dialer');
    this._ussdResponse.next(null);
    this._isLoading.next(false);
    this._error.next(null);
  }

  handleHome(): void {
    this.release();
    this._screen.next('dialer');
    this._phoneNumber.next('');
    this._dialedCode.next('');
    this._sessionId.next(null);
    this._ussdResponse.next(null);
    this._isLoading.next(false);
    this._error.next(null);
    this._currentServiceCode.next('');
    this._isConnecting.next(false);
    this._sessionState.next('idle');
    this._sessionStartedAt.next(null);
  }

  clearTranscript(): void {
    this._transcript.next([]);
    if (!this.isSessionLive) {
      this._sessionState.next('idle');
      this._sessionStartedAt.next(null);
      this._sessionId.next(null);
    }
  }

  clearError(): void {
    this._error.next(null);
  }

  // ── Test number ──────────────────────────────────────────

  setPhoneNumber(number: string): void {
    this._phoneNumber.next(number);
  }

  setSelectedCountry(country: CountryInfo): void {
    this.countryChosen = true;
    this._selectedCountry.next(country);
    this._phoneNumber.next('');
    this._operator.next('');
  }

  /** Set the country without clearing the number (restoring a saved number, defaults). */
  setCountry(country: CountryInfo, chosen = true): void {
    if (chosen) this.countryChosen = true;
    if (country.code !== this._selectedCountry.getValue().code) {
      this._operator.next('');
    }
    this._selectedCountry.next(country);
  }

  setOperator(code: string): void {
    this._operator.next(code ?? '');
  }

  // ── Requests ─────────────────────────────────────────────

  private send(kind: UssdTurn['kind'], input: string, request: UssdServiceRequest): void {
    const turn: UssdTurn = {
      id: this.nextTurnId++,
      sessionId: request.sessionId,
      kind,
      input,
      sentAt: new Date(),
      request,
      response: null,
      httpStatus: null,
      error: null,
      latencyMs: null,
      pending: true,
    };
    this.pushTurn(turn);
    const started = now();

    this.ussdService.sendRequest(request).pipe(take(1)).subscribe({
      next: (response) => {
        const latencyMs = Math.round(now() - started);
        const failed = this.ussdService.hasErrorMetadata(response);
        this.updateTurn(turn.id, {
          response,
          latencyMs,
          pending: false,
          error: failed ? response.serviceData || 'The application reported an error.' : null,
        });
        if (kind === 'Release' || !this.isAwaiting(request)) return;
        this.applyResponse(response, failed);
      },
      error: (err: unknown) => {
        const latencyMs = Math.round(now() - started);
        const message = this.describeError(err, kind === 'New' ? 'dial' : 'send');
        const status = err instanceof HttpErrorResponse ? err.status : null;
        this.updateTurn(turn.id, {
          latencyMs,
          pending: false,
          httpStatus: status,
          error: message,
          response:
            err instanceof HttpErrorResponse && err.error && typeof err.error === 'object'
              ? (err.error as UssdServiceResponse)
              : null,
        });
        if (kind === 'Release' || !this.isAwaiting(request)) return;
        this._isLoading.next(false);
        this._sessionState.next('failed');
        this._error.next(message);
        this._ussdResponse.next({ serviceData: message, serviceRequestType: 'Release' });
      },
    });
  }

  /**
   * Whether the session still waits for this request's answer. After Home,
   * Cancel or leaving the page, release() has already ended it, and a late
   * answer must not reopen it.
   */
  private isAwaiting(request: UssdServiceRequest): boolean {
    return request.sessionId === this._sessionId.getValue() && this.isSessionLive;
  }

  private applyResponse(response: UssdServiceResponse, failed: boolean): void {
    const type = response.serviceRequestType;
    const ends = failed || type === 'Release' || type === 'Timeout';

    if (failed) {
      this._error.next(response.serviceData || 'The application reported an error.');
      response = { ...response, serviceRequestType: 'Release' };
    }

    this._ussdResponse.next(response);
    this._isLoading.next(false);
    this._sessionState.next(failed ? 'failed' : ends ? 'ended' : 'open');
  }

  private describeError(err: unknown, action: 'dial' | 'send'): string {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { serviceData?: unknown; message?: unknown; title?: unknown } | null;
      const fromBody =
        (body && typeof body === 'object' && (body.serviceData || body.message || body.title)) || '';
      if (typeof fromBody === 'string' && fromBody.trim()) return fromBody.trim();
      if (err.status === 0) {
        return 'Could not reach the USSD simulator. Check your connection and try again.';
      }
      if (err.status === 404) {
        return 'Nothing answered this code. Check the code and that its application is enabled.';
      }
      if (err.status >= 500) {
        return `The simulator failed to ${action === 'dial' ? 'run the code' : 'send your reply'} (error ${err.status}). Try again in a moment.`;
      }
      return `The simulator rejected the request (error ${err.status}).`;
    }
    return action === 'dial' ? 'Could not run the code. Try again.' : 'Could not send your reply. Try again.';
  }

  private pushTurn(turn: UssdTurn): void {
    const list = [...this._transcript.getValue(), turn];
    this._transcript.next(list.length > MAX_TURNS ? list.slice(list.length - MAX_TURNS) : list);
  }

  private updateTurn(id: number, patch: Partial<UssdTurn>): void {
    this._transcript.next(
      this._transcript.getValue().map((turn) => (turn.id === id ? { ...turn, ...patch } : turn))
    );
  }

  private buildMsisdn(): string {
    const phoneNumber = this._phoneNumber.getValue();
    const country = this._selectedCountry.getValue();
    if (phoneNumber.startsWith('+')) {
      return phoneNumber.replace(/\s/g, '');
    }
    return (country.dialCode + phoneNumber.replace(/^0+/, '')).replace(/\s/g, '');
  }
}
